import { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { submit } from 'redux-form';

import { DataRequestActionTypes } from '../../constants/actionTypes';
import { FormId } from '../../constants/formId';
import * as FormHelper from '../../helpers/formHelper';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';
import AppForm from '../form/appForm';
import AppPartialSubmitForm from '../form/appPartialSubmitForm';

import AppModal from './appModal';

interface FormModalOwnProps {
  formTitle: string;
  formId: FormId;
  submitActionType: DataRequestActionTypes;
  successMessage?: any;
  onSuccessCallback?: () => void;
  validateForm?: any;
  reduxData?: any;
  initialValues?: any;
  partialSubmit?: boolean;
  showSendAnotherCheckbox?: boolean;
  preprocessDataOnSubmit?: (formData: any) => any;
  buttonIcon?: IconProp;
  invertedButtonColours?: boolean;
  smallButton?: boolean;
  hideButton?: boolean;
  onClosed?: () => void;
  noFooterButtons?: boolean;
  disabled?: boolean;
}

interface FormModalStateProps extends BaseStateProps {
  formValues: any;
  submitting: boolean;
  submittedSuccessfully: boolean;
  open: boolean;
  modalProps?: any;
}

interface FormModalDispatchProps extends BaseDispatchProps {
  submitForm: () => void;
}

class FormModal extends ComponentBase<FormModalOwnProps, FormModalStateProps, FormModalDispatchProps> {
  private modal: any;

  // eslint-disable-next-line camelcase
  public async UNSAFE_componentWillUpdate(props: any) {
    await super.UNSAFE_componentWillUpdate(props);

    if (this.props.submittedSuccessfully) {
      if (!this.modal.state.sendAnother) {
        this.modal.close();
      }
    }

    if (this.props.submitting || this.props.noFooterButtons) {
      this.modal.hideButtons();
    } else {
      this.modal.showButtons();
    }
  }

  protected loggedInRender() {
    return (
      <AppModal
        id={this.props.formId}
        title={this.props.formTitle}
        submitAction={this.props.submitForm}
        ref={(r: any) => {
          this.modal = r ? r.getWrappedInstance() : r;
        }}
        buttonIcon={this.props.buttonIcon}
        showSendAnotherCheckbox={this.props.showSendAnotherCheckbox}
        invertedButtonColours={this.props.invertedButtonColours}
        smallButton={this.props.smallButton}
        hideOpenButton={this.props.hideButton}
        onClosed={this.props.onClosed}
        disabled={this.props.disabled}
      >
        {this.renderForm()}
      </AppModal>
    );
  }

  private renderForm(): JSX.Element {
    const FormType = this.props.partialSubmit ? AppPartialSubmitForm : AppForm;

    return (
      <FormType
        form={this.props.formId}
        submitActionType={this.props.submitActionType}
        hideButtons
        validate={this.props.validateForm}
        reduxData={{
          ...this.props.reduxData,
          ...this.props.modalProps,
        }}
        successMessage={this.props.successMessage}
        initialValues={this.props.modalProps || this.props.initialValues}
        preprocessDataOnSubmit={this.props.preprocessDataOnSubmit}
        onSuccess={this.props.onSuccessCallback}
      >
        {this.props.children}
      </FormType>
    );
  }
}

function mapStateToProps(state: ClippingPlatformState, ownProps: FormModalOwnProps): FormModalStateProps {
  const formData = state.form[ownProps.formId];
  const modalData = state.appStatus.modals[ownProps.formId];

  return {
    ...baseMapStateToProps(state),
    formValues: formData ? formData.values : undefined,
    modalProps: modalData ? modalData.props : undefined,
    open: FormHelper.isFormOpen(formData),
    submittedSuccessfully: FormHelper.didSubmitSuccessfully(formData),
    submitting: FormHelper.isFormSubmitting(formData),
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: FormModalOwnProps): FormModalDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    submitForm: () => dispatch(submit(ownProps.formId)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormModal);
